import { Skeleton } from '@repo/ui';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { hasAppAccess } from '@/hooks/auth/has-app-access';
import { useCurrentUser } from '@/hooks/auth/use-current-user';
import { useSessionSync } from '@/hooks/auth/use-session-sync';
import ServerUnreachablePage from '@/pages/login/ServerUnreachablePage';
import { redirectToSignIn } from '@/shared/api/redirect-to-sign-in';
import { selectCurrentUser, sessionEstablished } from '@/store/auth.slice';
import { useAppDispatch, useAppSelector } from '@/store/store';

import { FrontDoor } from './front-door';

/**
 * There is no token to inspect — the cookie is httpOnly. Whether a session
 * exists is answered by the server, and an unauthenticated caller is sent to
 * the gateway to sign in.
 */
export function RequireAuth() {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const storedUser = useAppSelector(selectCurrentUser);
  const { data: user, error, isError, refetch } = useCurrentUser();
  const isUnauthorized = isAxiosError(error) && error.response?.status === 401;

  useSessionSync();

  useEffect(() => {
    if (user) dispatch(sessionEstablished(user));
  }, [user, dispatch]);

  useEffect(() => {
    if (isUnauthorized) redirectToSignIn('the account lookup was refused');
  }, [isUnauthorized]);

  // A guarded page reads its permissions from the store, never from this
  // query, so it must not render until the store holds *this* fetch's
  // result. `isPending` alone is not enough to gate on: the dispatch above
  // runs in an effect, one commit after the query resolves, and a page that
  // mounts inside that gap sees an empty permission set and navigates itself
  // away before the effect ever fires — which is the whole bug. Comparing by
  // reference to the store's user, rather than just checking "some user is
  // present", also covers a mid-session refetch: if permissions change under
  // a live session, this holds the gate shut on the outgoing (stale) set
  // until the fresh result is the one in the store, instead of letting a
  // page render for one frame on data the server has already superseded.
  const ready = Boolean(user) && storedUser === user;

  // A blank tab reads as a hang, and both branches below are transient: an
  // auth error redirects the whole page away within a tick, and a pending
  // fetch resolves within one. Neither should render nothing while it waits.
  if (isUnauthorized) return <AuthLoadingState />;

  // Anything but a 401 says nothing about whether a session exists, so it must
  // not start a sign-in round trip. A user already in hand outranks a failed
  // background refetch.
  if (isError && !user) return <ServerUnreachablePage onRetry={() => refetch()} />;
  if (!ready || !storedUser) return <AuthLoadingState />;

  // Authenticated with nothing this app uses. Rendering the shell here would
  // show a full navigation whose every screen answers 403, which reads as a
  // broken application rather than as an account waiting on a role. My access is the one
  // page open to everyone signed in, so a person can see and choose among their roles.
  if (!hasAppAccess(storedUser.permissions)) {
    if (pathname === APP_ROUTES.myAccess) return <Outlet />;

    return <FrontDoor email={storedUser.email} />;
  }

  return <Outlet />;
}

function AuthLoadingState() {
  return (
    <div
      className="flex h-full items-center justify-center px-4"
      role="status"
      aria-label="Loading your account"
    >
      <div className="flex w-full max-w-sm flex-col gap-3">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}
